import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone } from "lucide-react";
import { useScrolled } from "@/hooks/useScrolled";
import GoogleTranslate from "@/components/GoogleTranslate";
import { supabase } from "@/integrations/supabase/client";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { isEventPast, toSiteDateOnly } from "@/lib/eventDates";
import { HELOC_EVENT, HELOC_EVENT_SLUG } from "@/data/events";

const FALLBACK_PHONE_DISPLAY = "(781) 632-3936";
const toTel = (phone: string) => `tel:${phone.replace(/[^+\d]/g, "")}`;

interface AnnouncementItem {
  text: string;
  href?: string;
}

const FALLBACK_ITEMS: { date: string; text: string }[] = [
  { date: "2026-05-07", text: "Sell Paradise, Earn Commission · Thu, May 7 · 6 PM - 8 PM" },
  { date: "2026-05-09", text: "Buy in Paradise, Earn Income · Sat, May 9 · 2 PM - 4 PM" },
];

const buildFallback = (): AnnouncementItem[] => {
  const legacy = FALLBACK_ITEMS.filter((i) => !isEventPast(i.date)).map((i) => ({
    text: i.text,
    href: "/events",
  }));
  const heloc = !isEventPast(HELOC_EVENT.event_date) && HELOC_EVENT.announcement_text
    ? [{ text: HELOC_EVENT.announcement_text, href: `/events/${HELOC_EVENT_SLUG}` }]
    : [];
  return [...heloc, ...legacy];
};

const TopBar = () => {
  const scrolled = useScrolled(50);
  const [items, setItems] = useState<AnnouncementItem[]>(() => buildFallback());
  const [announcementIndex, setAnnouncementIndex] = useState(0);
  const { getSetting } = useSiteSettings();
  const phoneDisplay = getSetting("contact.phone", FALLBACK_PHONE_DISPLAY);
  const [announcementVisible, setAnnouncementVisible] = useState(true);

  // Fetch active announcements from Supabase; fallback stays in place on error/empty.
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [annRes, evRes] = await Promise.all([
          supabase
            .from("announcements")
            .select("title,link_url,sort_order")
            .order("sort_order", { ascending: true })
            .order("created_at", { ascending: false }),
          supabase
            .from("events")
            .select("title,date_time_label,time_range,event_date,sort_order")
            .eq("is_published", true)
            .eq("show_in_announcement_bar", true)
            .gte("event_date", toSiteDateOnly())
            .order("event_date", { ascending: true }),
        ]);
        if (!mounted) return;

        const remote: AnnouncementItem[] = [];
        if (!annRes.error && annRes.data) {
          for (const a of annRes.data) {
            if (a.title) remote.push({ text: a.title, href: a.link_url ?? undefined });
          }
        }
        if (!evRes.error && evRes.data) {
          for (const e of evRes.data) {
            if (e.title === HELOC_EVENT.title) continue;
            const parts = [e.title, e.date_time_label, e.time_range].filter(Boolean);
            remote.push({ text: parts.join(" · "), href: "/events" });
          }
        }

        if (remote.length > 0) {
          const staticItems = buildFallback().filter(
            (item) =>
              item.href === `/events/${HELOC_EVENT_SLUG}` &&
              !remote.some((remoteItem) => remoteItem.text === item.text || remoteItem.href === item.href)
          );
          setItems([...staticItems, ...remote]);
        }
      } catch {
        // keep fallback
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (items.length <= 1) return;
    const interval = window.setInterval(() => {
      setAnnouncementVisible(false);
      window.setTimeout(() => {
        setAnnouncementIndex((prev) => (prev + 1) % items.length);
        setAnnouncementVisible(true);
      }, 220);
    }, 5000);
    return () => window.clearInterval(interval);
  }, [items.length]);

  // Reset index if the items list shrinks
  useEffect(() => {
    if (announcementIndex >= items.length) setAnnouncementIndex(0);
  }, [items.length, announcementIndex]);

  const current = items[announcementIndex];
  const linkTo = current?.href || "/events";
  const isExternal = linkTo.startsWith("http");

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-[60] h-9 transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
        scrolled
          ? "bg-primary/95 backdrop-blur-xl border-b border-border/10"
          : "bg-white/[0.06] backdrop-blur-md border-b border-white/[0.08]"
      }`}
    >
      <div className="mx-auto max-w-[1520px] h-full px-6 md:px-10 lg:px-16 flex items-center justify-between">
        {/* Left — announcement */}
        {items.length > 0 && current ? (
          isExternal ? (
            <a
              href={linkTo}
              target="_blank"
              rel="noopener noreferrer"
              className={`min-w-0 max-w-[48%] overflow-hidden text-ellipsis whitespace-nowrap font-body text-[13px] font-medium tracking-[0.02em] transition-all duration-300 ${
                scrolled ? "text-primary-foreground/75" : "text-white/65"
              }`}
            >
              <span
                className={`inline-flex max-w-full items-center gap-1.5 transition-opacity duration-200 ${
                  announcementVisible ? "opacity-100" : "opacity-0"
                }`}
              >
                <span className="shrink-0 text-[#C9A95C]">Upcoming Event:</span>
                <span className="truncate">{current.text}</span>
              </span>
            </a>
          ) : (
            <Link
              to={linkTo}
              className={`min-w-0 max-w-[48%] overflow-hidden text-ellipsis whitespace-nowrap font-body text-[13px] font-medium tracking-[0.02em] transition-all duration-300 ${
                scrolled ? "text-primary-foreground/75" : "text-white/65"
              }`}
            >
              <span
                className={`inline-flex max-w-full items-center gap-1.5 transition-opacity duration-200 ${
                  announcementVisible ? "opacity-100" : "opacity-0"
                }`}
              >
                <span className="shrink-0 text-[#C9A95C]">Upcoming Event:</span>
                <span className="truncate">{current.text}</span>
              </span>
            </Link>
          )
        ) : null}

        {/* Right — phone + lang switcher */}
        <div className="flex items-center gap-5 ml-auto">
          <a
            href={toTel(phoneDisplay)}
            className={`inline-flex min-h-11 items-center gap-1.5 whitespace-nowrap font-body text-[12px] md:text-[13px] font-medium tracking-[0.02em] transition-colors duration-500 ${
              scrolled
                ? "text-primary-foreground/80 hover:text-primary-foreground"
                : "text-white/80 hover:text-white/95"
            }`}
          >
            <Phone size={12} strokeWidth={2} />
            <span className="whitespace-nowrap">{phoneDisplay}</span>
          </a>

          <span
            className={`hidden md:block w-px h-3.5 transition-colors duration-500 ${
              scrolled ? "bg-primary-foreground/20" : "bg-white/20"
            }`}
          />

          <div className="hidden md:flex items-center">
            <GoogleTranslate variant="dark" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopBar;
